import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import type { StatusTone } from "@makit/shared";

/* ── 버튼 ─────────────────────────────────────────────────── */

/** `ghost-danger` 는 **파괴적 동작의 트리거**다. 확인 창 안의 마지막 버튼만 채움(`danger`)
 *  이고 트리거는 테두리다 — 트리거까지 채우면 목록 화면이 "지우기"로 먼저 읽힌다. */
export type ButtonVariant = "primary" | "ghost" | "danger" | "ghost-danger";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  label: string;
  variant?: ButtonVariant;
  small?: boolean;
  /** 비활성 사유. 툴팁만 두지 않고 항상 보이는 텍스트로 옆에 쓴다. */
  disabledReason?: string;
}

export function Button({
  label,
  variant = "primary",
  small = false,
  disabledReason,
  disabled,
  ...rest
}: ButtonProps) {
  const cls = [
    "btn",
    variant === "ghost" || variant === "ghost-danger" ? "ghost" : "",
    variant === "danger" || variant === "ghost-danger" ? "danger" : "",
    small ? "small" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const button = (
    <button type="button" className={cls} disabled={disabled} {...rest}>
      {label}
    </button>
  );
  if (!disabled || !disabledReason) return button;
  return (
    <>
      {button}
      <span className="why">{disabledReason}</span>
    </>
  );
}

/* ── 상태 칩 — 점 + 텍스트를 항상 함께 쓴다 ─────────────────── */

export function StatusChip({ tone, label }: { tone: StatusTone; label: string }) {
  return <span className={`chip ${tone}`}>{label}</span>;
}

/* ── 투명 썸네일 ─────────────────────────────────────────── */

export interface CheckerThumbProps {
  size?: number;
  src?: string | null;
  alt?: string;
  /** 예시 실루엣 클래스. 실제 이미지가 없을 때만 쓴다. */
  sample?: string;
  /** 칸을 꽉 채운다. 인라인 크기를 주지 않아 격자(`pickgrid`)의 CSS 가 이긴다. */
  fill?: boolean;
}

export function CheckerThumb({ size = 34, src, alt = "", sample, fill = false }: CheckerThumbProps) {
  return (
    <span className="thumb" style={fill ? undefined : { width: size, height: size }}>
      {src ? <img src={src} alt={alt} /> : sample ? <span className={sample} /> : null}
    </span>
  );
}

/* ── 브랜드마크 ───────────────────────────────────────────
   **`✂` 글리프를 대체한다.** 가위는 "배경을 자른다"는 수단을 그렸는데, 이 제품이 파는 것은
   자르는 일이 아니라 팔 수 있게 된 상품이다. 그래서 마크는 상품(원피스)과 그것을 갖춘
   상태(진주·반짝임)를 그린다.

   도형은 목업 ver2 `.brandmark` 의 인라인 SVG 를 그대로 옮긴 것이고, 앱
   (`apps/mobile/src/components/brand-mark.tsx`)이 같은 path 를 들고 있다. 고칠 일이 생기면
   목업을 먼저 고치고 양쪽을 함께 옮긴다. 상자는 CSS(`.brandmark` / `.mk`)가 그리고 색은
   `currentColor` 로 상자에서 받는다 — 상자마다 배경이 다를 수 있다. */

/** 안쪽 그림은 상자의 23/40 을 유지한다(목업 40px 상자 안 23px SVG). */
export function BrandMark({ size = 23 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* 원피스 */}
      <path
        d="M9.3 4.1 6.5 5.6 5.3 8.1l2.1.9 1-1.8-1.7 10.4c3.4 1.5 6.9 1.5 10.6 0L15.6 7.2l1 1.8 2.1-.9-1.2-2.5-2.8-1.5c-1 1.3-4.4 1.3-5.4 0Z"
        fill="currentColor"
      />
      {/* 진주 목걸이 — 줄과 알 셋 */}
      <path
        d="M5.6 18.4c2 3 8.6 3.9 12.2-.6"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        opacity=".85"
      />
      <circle cx="7.3" cy="20.1" r=".8" fill="currentColor" />
      <circle cx="10.6" cy="21.2" r=".8" fill="currentColor" />
      <circle cx="14.1" cy="21" r=".8" fill="currentColor" />
      {/* 반짝임 */}
      <path
        d="M19.3 2.6l.85 1.75 1.75.85-1.75.85-.85 1.75-.85-1.75L16.7 5.2l1.75-.85z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ── 미정 배지 ───────────────────────────────────────────── */

export function Tbd({ label = "미정" }: { label?: string }) {
  return <span className="tbd">{label}</span>;
}

/* ── 출처 배지 ───────────────────────────────────────────── */

export function SourceBadge({ origin }: { origin: string }) {
  return <span className="src">{origin}</span>;
}

/* ── 필터 칩 ─────────────────────────────────────────────── */

export interface FilterChipProps {
  label: string;
  on?: boolean;
  /** 다중 선택 칩만 켜졌을 때 앞에 체크가 붙는다. 단일 선택은 배경색만 바뀐다. */
  multi?: boolean;
  /** 걸린 조건이면 뒤에 해제 표시를 붙인다. */
  removable?: boolean;
  onClick?: () => void;
}

export function FilterChip({ label, on = false, multi = false, removable = false, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      className={`fchip${on ? " on" : ""}`}
      aria-pressed={on}
      onClick={onClick}
      aria-label={removable ? `${label} 조건 해제` : label}
    >
      {multi && on ? (
        <span className="ck" aria-hidden="true">
          ✓
        </span>
      ) : null}
      {label}
      {removable ? (
        <span className="ck x" aria-hidden="true">
          ✕
        </span>
      ) : null}
    </button>
  );
}

/* ── 스위치 · 스와치 · 입력칸 ───────────────────────────────── */

export function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`${label} ${on ? "켜짐" : "꺼짐"}`}
      className={`sw${on ? " on" : ""} hit44`}
      onClick={() => onChange(!on)}
    />
  );
}

/** 배경 견본 하나.
 *
 * ⚠️ **`hex` 가 `null` 인 자리가 있다.** 팔레트에 이름만 있고 값이 아직 없는 색이다
 * (`BACKGROUND_OPTIONS` — 아이보리·쿨그레이). 그런 칸은 **고를 수 없어야 한다**: 서버가
 * 팔레트 밖 값을 422 로 거절하므로, 누를 수 있게 두면 셀러가 눌러 보고 실패한다.
 * `미정` 배지만으로는 그 인상을 이기지 못한다 — 견본은 **누를 수 있게 생긴 것**이라
 * 배지보다 모양이 먼저 읽힌다(PRD 7.5.8 의 비활성 규칙). */
export function Swatch({
  hex,
  name,
  selected,
  disabled = false,
  onSelect,
}: {
  hex: string | null;
  name: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`sws${selected ? " sel" : ""}${disabled ? " off" : ""} hit44`}
      // 값이 없는 칸은 색으로 칠할 것이 없다. 아무 색이나 넣으면 **정해지지 않은 색을
      // 정해진 것처럼** 보여주게 된다.
      style={hex ? { background: hex } : undefined}
      aria-pressed={selected}
      aria-label={hex ? `${name} ${hex}` : `${name} (아직 정해지지 않음)`}
      disabled={disabled}
      onClick={onSelect}
    />
  );
}

export function Field(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="field" {...props} />;
}

/* ── 스켈레톤 · 진행 막대 ───────────────────────────────────── */

export function Skeleton({ width = "100%", height = 10 }: { width?: string | number; height?: number }) {
  return <span className="sk line" style={{ width, height, display: "block" }} aria-hidden="true" />;
}

/** 비율은 shared 의 `jobProgress` 가 계산한다 — 여기서 다시 세지 않는다. */
export function ProgressBar({ ratio, label }: { ratio: number; label?: string }) {
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <span
      className="prog"
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ display: "block" }}
    >
      <i style={{ width: `${clamped * 100}%` }} />
    </span>
  );
}

/* ── 힌트 ────────────────────────────────────────────────── */

export function Hint({ children }: { children: ReactNode }) {
  return <p className="hint">{children}</p>;
}
